import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Observable, Subject, catchError, map, of, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { visitReasonFromSoap } from '../shared/utils/visit-reason.util';
import { createNoCacheOptions } from '../utils/http-headers.util';

export interface Transcription {
  id: string;
  date: string;
  specialty: string;
  chiefComplaint?: string;
  duration: string;
  status: string;
  patientId?: string;
  patientName?: string;
  mrn?: string;
  content?: string;
  appointmentType?: string;
  appointmentTime?: string | Date; // Add appointmentTime for precise sorting
  /** S3 object key for the primary recording (e.g. {@code recordings/final-{uuid}.mp3}). */
  originalFileName?: string;
  /** Persisted SOAP exists with clinical content. */
  soapNotesAvailable?: boolean;
  /** SOAP generation is newer than last review (only when {@link soapNotesAvailable}). */
  soapUnread?: boolean;
  /** 2–3 word reason for the visit (problem name or clipped chief complaint). */
  visitReason?: string;
}

/** Synthetic id for the upload-in-progress row in the transcriptions table (not from API). */
export const UPLOAD_SPINNER_ROW_ID = '__upload_spinner__';

/** Optimistic Visits list row while stop/upload has navigated away but the API row is not visible yet. */
export const PENDING_RECORDING_VISIT_ID = '__pending_recording__';

export function isFailedTranscriptionStatus(status: string | undefined): boolean {
  return (status ?? '').trim().toLowerCase() === 'failed';
}

export function isUploadSpinnerTableRow(row: Pick<Transcription, 'id'>): boolean {
  return row.id === UPLOAD_SPINNER_ROW_ID;
}

export function isPendingRecordingVisitRow(row: Pick<Transcription, 'id'>): boolean {
  return row.id === PENDING_RECORDING_VISIT_ID;
}

/** Per-problem Assessment and Plan (Visits canonical shape). */
export interface SoapProblem {
  index?: number;
  name: string;
  assessment?: string;
  plan?: string[];
}

/** True when the stored object is the canonical merged MP3 from checkpoint recording (extendable on server). */
export function isCheckpointFinalRecordingKey(name: string | undefined | null): boolean {
  return parseCheckpointFinalRecordingUuid(name) != null;
}

/** Lowercase UUID from {@code recordings/final-{uuid}.mp3}, or null if the key is not in that form. */
export function parseCheckpointFinalRecordingUuid(name: string | undefined | null): string | null {
  if (!name?.trim()) {
    return null;
  }
  const m = name.trim().match(/^recordings\/final-([0-9a-f-]{36})\.mp3$/i);
  return m ? m[1].toLowerCase() : null;
}

/** Payload for PUT /medical-analysis/transcription/{id}/notes */
export interface SaveTranscriptionClinicalNotesPayload {
  subjective: string;
  objective: string;
  patientInstructions: string;
  problems: SoapProblem[];
  assessmentSummary?: string;
  keyFindings?: string[];
  treatmentPlanSummary?: string[];
}

export interface TranscriptionDetailResponse {
  id: string;
  originalFileName: string;
  transcriptionUrl: string;
  status: string;
  specialty: string;
  analysisId?: string;
  error?: string;
  patientId: number;
  date?: string; // Add date property for template compatibility
  duration?: string; // Add duration property for template compatibility
  transcriptText?: string;
  transcriptTranslated?: string;
  transcriptLanguage?: string;
  
  // Appointment fields
  appointmentType?: string;
  chiefComplaint?: string;
  appointmentTime?: string | Date;
  createdByUserName?: string;
  createdByUserId?: string;
  
  // Patient information
  patientName: string;
  mrn: string;
  dateOfBirth?: string;
  patientActive: boolean;
  
  // Analysis results
  analysis?: {
    id: string;
    transcriptUrl?: string;
    summary?: string;
    diagnosis?: string;
    prescription?: {
      medicationName?: string;
      dosage?: string;
      frequency?: string;
      duration?: string;
      instructions?: string;
    };
    soapNote?: {
      subjective?: string;
      objective?: string;
      patientInstructions?: string;
      assessmentSummary?: string;
      problems?: SoapProblem[];
    };
    aiAnalysis?: {
      keyFindings?: string[];
      treatmentPlanSummary?: string[];
    };
    specialty?: string;
    analysisData?: string;
    status?: string;
  };
  soapNotesAvailable?: boolean;
  soapUnread?: boolean;
  visitReason?: string;
}

export interface UpdateTranscriptionMetadataRequest {
  patientName: string;
  appointmentType: string;
  patientId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class TranscriptionsService {
  private baseUrl = environment.apiUrl;
  private apiKey = environment.apiKey;

  /** Other screens (e.g. recording upload) can signal the list to reload. */
  private readonly listRefreshRequests = new Subject<void>();
  readonly transcriptionsListRefreshRequested$ = this.listRefreshRequests.asObservable();

  /** While true, the transcriptions table shows a spinner as the first row (upload in flight). */
  readonly recordingUploadAwaitingResult = signal(false);

  /**
   * Set when stop/upload fails after navigating away from recording (component-scoped toasts are gone).
   * Visits / list UIs can show this once, then clear.
   */
  readonly recordingUploadError = signal<string | null>(null);

  /**
   * Optimistic visit card shown on Visits immediately after Stop navigates away,
   * until the real transcription row appears in the list.
   */
  readonly pendingRecordingVisit = signal<Transcription | null>(null);

  /** Stop waiting for a new row after this long — prevents endless list polling when finalize never succeeds. */
  private static readonly UPLOAD_AWAITING_MAX_MS = 90_000;
  private uploadAwaitingStartedAtMs: number | null = null;

  /** After upload HTTP succeeds, clear spinner once the next list refresh finishes (success or error). */
  private clearRecordingUploadAwaitingAfterNextRefresh = false;

  /** When present, clear spinner as soon as a list fetch includes this transcription id (pipeline row visible). */
  private recordingUploadSpinnerLinkedTranscriptionId: string | null = null;

  /** After pause/stop, Visits should select this id once it appears in the list. */
  private selectVisitOnLoadId: string | null = null;

  /** Visit id currently regenerating notes after stop/append/resume (force drafting UI). */
  readonly notesBuildingVisitId = signal<string | null>(null);
  /** True once the building visit has left Completed for a pipeline status (Analyzing, etc.). */
  private notesBuildingSawPipeline = false;
  /**
   * True when notes are being rebuilt on an existing visit (reanalyze / append / resume).
   * False for a brand-new recording — Completed + SOAP means done even if we missed Analyzing.
   */
  private notesBuildingIsRebuild = false;

  constructor(private http: HttpClient) {}

  requestTranscriptionsListRefresh(): void {
    this.listRefreshRequests.next();
  }

  markNotesBuilding(
    id: string | number | null | undefined,
    options?: { rebuild?: boolean }
  ): void {
    if (id == null || id === '') {
      this.notesBuildingVisitId.set(null);
      this.notesBuildingSawPipeline = false;
      this.notesBuildingIsRebuild = false;
      return;
    }
    const s = String(id).trim();
    if (!s) {
      this.notesBuildingVisitId.set(null);
      this.notesBuildingSawPipeline = false;
      this.notesBuildingIsRebuild = false;
      return;
    }
    const sameVisit = this.notesBuildingVisitId() === s;
    this.notesBuildingVisitId.set(s);
    // Finalize re-marks the same visit with no options. Keep rebuild / pipeline-seen so a
    // still-Completed row does not flash the note from before Stop.
    if (sameVisit && options?.rebuild == null) {
      return;
    }
    this.notesBuildingSawPipeline = false;
    this.notesBuildingIsRebuild = options?.rebuild === true;
  }

  clearNotesBuilding(id?: string | number | null): void {
    if (id == null) {
      this.notesBuildingVisitId.set(null);
      this.notesBuildingSawPipeline = false;
      this.notesBuildingIsRebuild = false;
      return;
    }
    if (this.notesBuildingVisitId() === String(id).trim()) {
      this.notesBuildingVisitId.set(null);
      this.notesBuildingSawPipeline = false;
      this.notesBuildingIsRebuild = false;
    }
  }

  isNotesBuilding(id: string | number | null | undefined): boolean {
    if (id == null) {
      return false;
    }
    return this.notesBuildingVisitId() === String(id).trim();
  }

  /** Rebuilds must wait for Analyzing before trusting Completed; new recordings must not. */
  isNotesBuildingRebuild(): boolean {
    return this.notesBuildingIsRebuild && this.notesBuildingVisitId() != null;
  }

  /** Call when list/detail shows Analyzing / Transcribing / In Progress for the building visit. */
  markNotesBuildingPipelineSeen(id: string | number | null | undefined): void {
    if (id == null || !this.isNotesBuilding(id)) {
      return;
    }
    this.notesBuildingSawPipeline = true;
  }

  /** True once pipeline progress was observed; safe to show SOAP again when Completed + ready. */
  hasNotesBuildingSeenPipeline(id: string | number | null | undefined): boolean {
    return this.isNotesBuilding(id) && this.notesBuildingSawPipeline;
  }

  setRecordingUploadAwaitingResult(value: boolean): void {
    if (value) {
      this.recordingUploadSpinnerLinkedTranscriptionId = null;
      this.recordingUploadError.set(null);
      this.uploadAwaitingStartedAtMs = Date.now();
    } else {
      this.recordingUploadSpinnerLinkedTranscriptionId = null;
      this.uploadAwaitingStartedAtMs = null;
      // Leave the optimistic visit in place. Clearing it here, before Visits selects the
      // real row, flashes the patient search. Visits drops it in the same turn it selects.
    }
    this.recordingUploadAwaitingResult.set(value);
  }

  /** Show an immediate Visits list row after Stop, before the API transcription exists. */
  seedPendingRecordingVisit(patientName?: string | null): void {
    const name = (patientName ?? '').trim() || 'Unknown';
    const now = new Date();
    this.pendingRecordingVisit.set({
      id: PENDING_RECORDING_VISIT_ID,
      date: now.toISOString(),
      appointmentTime: now,
      specialty: '',
      duration: '',
      status: 'Analyzing',
      patientName: name,
      mrn: '',
      chiefComplaint: 'Building notes…',
    });
  }

  clearPendingRecordingVisit(): void {
    this.pendingRecordingVisit.set(null);
  }

  /**
   * True while a post-stop upload is in flight and within the timeout window.
   * Auto-clears the awaiting flag if the deadline passed (no new visit row appeared).
   * Timeout only applies when we are still waiting for a brand-new optimistic row to show up —
   * not while notes are building on an already-linked visit.
   */
  isRecordingUploadAwaitingActive(): boolean {
    if (!this.recordingUploadAwaitingResult()) {
      return false;
    }
    if (
      this.uploadAwaitingStartedAtMs != null &&
      Date.now() - this.uploadAwaitingStartedAtMs > TranscriptionsService.UPLOAD_AWAITING_MAX_MS
    ) {
      // Linked existing visit (resume/append) means processing is underway — do not scare with timeout.
      if (this.recordingUploadSpinnerLinkedTranscriptionId) {
        this.clearRecordingUploadAwaiting('dismiss');
        return false;
      }
      this.clearRecordingUploadAwaiting(
        'timeout',
        'Recording processing timed out. The visit may not have been saved — try recording again.'
      );
      return false;
    }
    return true;
  }

  /**
   * Clear the upload-awaiting spinner / poll gate. Use on failure or timeout so list UIs stop waiting.
   */
  clearRecordingUploadAwaiting(
    reason: 'failure' | 'timeout' | 'dismiss' = 'dismiss',
    message?: string
  ): void {
    this.clearRecordingUploadAwaitingAfterNextRefresh = false;
    this.setRecordingUploadAwaitingResult(false);
    if (reason === 'failure' || reason === 'timeout') {
      this.clearPendingRecordingVisit();
      this.recordingUploadError.set(
        message?.trim() ||
          (reason === 'timeout'
            ? 'Recording processing timed out. The visit may not have been saved — try recording again.'
            : 'Recording could not be processed. The visit was not saved.')
      );
    }
  }

  clearRecordingUploadError(): void {
    this.recordingUploadError.set(null);
  }

  /** Bind spinner dismissal to the transcription row returned by finalize / upload (optional id). */
  setRecordingUploadSpinnerLinkedRow(id: string | number | null | undefined): void {
    if (id == null || id === '') {
      this.recordingUploadSpinnerLinkedTranscriptionId = null;
      return;
    }
    const s = String(id).trim();
    this.recordingUploadSpinnerLinkedTranscriptionId = s.length ? s : null;
    this.selectVisitOnLoadId = s.length ? s : null;
  }

  getRecordingUploadSpinnerLinkedRow(): string | null {
    return this.recordingUploadSpinnerLinkedTranscriptionId;
  }

  requestSelectVisitOnLoad(id: string | number | null | undefined): void {
    if (id == null || id === '') {
      this.selectVisitOnLoadId = null;
      return;
    }
    const s = String(id).trim();
    this.selectVisitOnLoadId = s.length ? s : null;
  }

  peekSelectVisitOnLoad(): string | null {
    return this.selectVisitOnLoadId;
  }

  clearSelectVisitOnLoad(): void {
    this.selectVisitOnLoadId = null;
  }

  /** True if finalize/upload succeeded and we are waiting for a list fetch to drop the upload spinner. */
  peekRecordingUploadClearScheduled(): boolean {
    return this.clearRecordingUploadAwaitingAfterNextRefresh;
  }

  /** Call when upload-and-transcribe HTTP succeeds, before requestTranscriptionsListRefresh(). */
  scheduleClearRecordingUploadAwaitingAfterRefresh(): void {
    this.clearRecordingUploadAwaitingAfterNextRefresh = true;
  }

  consumeClearRecordingUploadAwaitingAfterRefresh(): boolean {
    const v = this.clearRecordingUploadAwaitingAfterNextRefresh;
    this.clearRecordingUploadAwaitingAfterNextRefresh = false;
    return v;
  }

  getAllTranscriptions(): Observable<Transcription[]> {
    return this.http.get<TranscriptionDetailResponse[]>(`${this.baseUrl}/transcriptions`, 
      createNoCacheOptions({ 'api-key': this.apiKey })
    ).pipe(
      map(transcriptions => {
        const converted = transcriptions.map(t => this.convertToTranscription(t));
        return converted;
      }),
      catchError(error => {
        console.error('TranscriptionsService: Error loading transcriptions:', error);
        return of([]);
      })
    );
  }

  getTranscriptionById(id: string): Observable<TranscriptionDetailResponse | null> {
    return this.http.get<TranscriptionDetailResponse>(`${this.baseUrl}/transcription-results/${id}`, 
      createNoCacheOptions({ 'api-key': this.apiKey })
    ).pipe(
      map(response => {
        // Add missing fields if not present (for template compatibility)
        if (response) {
          if (!response.date) {
            response.date = new Date().toISOString().split('T')[0]; // Use current date as fallback
          }
          if (!response.duration) {
            response.duration = 'Unknown'; // Use fallback value for duration
          }
        }
        return response;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('TranscriptionsService: Error loading transcription by ID:', error);
        if (error.status === 404) {
          console.error('Transcription not found:', id);
        }
        return of(null);
      })
    );
  }

  getTranscriptionsByPatientId(patientId: string): Observable<Transcription[]> {
    return this.getAllTranscriptions().pipe(
      map(transcriptions => transcriptions.filter(t => t.patientId === patientId))
    );
  }

  /**
   * Re-analyze an existing transcription with updated medical analysis
   */
  reanalyzeTranscription(transcriptionId: string, specialty?: string): Observable<any> {
    const url = `${this.baseUrl}/medical-analysis/reanalyze/${transcriptionId}`;
    const params: any = {};
    
    if (specialty) {
      params.specialty = specialty;
    }

    return this.http.post(url, {}, {
      headers: {
        'api-key': this.apiKey
      },
      params: params
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('TranscriptionsService: Error re-analyzing transcription:', error);
        throw error;
      })
    );
  }

  /**
   * Fetch the full transcript content from S3
   * @param transcriptionId The transcription result ID
   * @returns Observable<string> The full transcript text
   */
  /**
   * Persist SOAP + visit summary / treatment plan edits to the medical analysis record.
   */
  saveTranscriptionClinicalNotes(
    transcriptionResultId: string,
    body: SaveTranscriptionClinicalNotesPayload
  ): Observable<void> {
    return this.http.put<void>(
      `${this.baseUrl}/medical-analysis/transcription/${transcriptionResultId}/notes`,
      body,
      createNoCacheOptions({ 'api-key': this.apiKey })
    );
  }

  /** Clears SOAP unread on lists after the clinician opens SOAP notes for this transcription. */
  markSoapNotesRead(transcriptionResultId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/medical-analysis/transcription/${transcriptionResultId}/soap-read`,
      {},
      createNoCacheOptions({ 'api-key': this.apiKey })
    );
  }

  updateTranscriptionMetadata(
    transcriptionResultId: string,
    body: UpdateTranscriptionMetadataRequest
  ): Observable<TranscriptionDetailResponse> {
    return this.http.put<TranscriptionDetailResponse>(
      `${this.baseUrl}/transcriptions/${transcriptionResultId}/metadata`,
      body,
      createNoCacheOptions({ 'api-key': this.apiKey })
    );
  }

  deleteTranscription(transcriptionResultId: string): Observable<void> {
    return this.http
      .delete<void>(
        `${this.baseUrl}/transcriptions/${encodeURIComponent(transcriptionResultId)}`,
        createNoCacheOptions({ 'api-key': this.apiKey })
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('TranscriptionsService: Error deleting transcription:', error);
          return throwError(() => error);
        })
      );
  }

  getTranscriptContent(transcriptionId: string, language?: 'original' | 'translated'): Observable<string> {
    const url = `${this.baseUrl}/transcription-results/${transcriptionId}/transcript`;
    const params = language ? { language } : undefined;
    
    return this.http.get(url, {
      headers: {
        'api-key': this.apiKey
      },
      params,
      responseType: 'text'
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('TranscriptionsService: Error fetching transcript content:', error);
        throw error;
      })
    );
  }

  private convertToTranscription(transcriptionDetail: TranscriptionDetailResponse): Transcription {
    return {
      id: transcriptionDetail.id != null ? String(transcriptionDetail.id) : '',
      date: transcriptionDetail.date || new Date().toISOString().split('T')[0],
      specialty: transcriptionDetail.specialty || 'General',
      duration: transcriptionDetail.duration || 'Unknown',
      status: transcriptionDetail.status,
      patientId:
        transcriptionDetail.patientId != null ? String(transcriptionDetail.patientId) : undefined,
      patientName: transcriptionDetail.patientName,
      mrn: transcriptionDetail.mrn,
      content: transcriptionDetail.transcriptionUrl,
      appointmentType: transcriptionDetail.appointmentType,
      appointmentTime: transcriptionDetail.appointmentTime ? 
        (typeof transcriptionDetail.appointmentTime === 'string' ? 
          transcriptionDetail.appointmentTime : 
          transcriptionDetail.appointmentTime.toString()) : undefined,
      originalFileName: transcriptionDetail.originalFileName,
      soapNotesAvailable: transcriptionDetail.soapNotesAvailable === true,
      soapUnread: transcriptionDetail.soapUnread === true,
      visitReason:
        transcriptionDetail.visitReason?.trim()
        || visitReasonFromSoap(transcriptionDetail.analysis?.soapNote)
        || undefined,
    };
  }

}
